<template>
    <CDropdown ref="dropdown" class="CButtonCombined" padding-none placement="right">
        <template #trigger="{ isOpen }">
            <CButton class="w-full" :icon="activeItem?.icon" :size="size" :variant="button.variant">
                <div class="flex items-center">
                    {{ button.text }}
                    <CTooltip v-if="tooltip" class="pl-1">
                        {{ tooltip }}
                    </CTooltip>
                    <span
                        class="mx-2 flex w-px self-stretch opacity-20"
                        :class="isDarkVariant ? 'bg-white dark:bg-surface-card' : 'bg-black'"
                    >
                    </span>
                    <CIcon :name="isOpen ? 'dropdown-up' : 'dropdown-down'" />
                </div>
            </CButton>
        </template>

        <template #content>
            <div class="p-2.5 min-w-64">
                <div
                    v-for="(item, index) in items"
                    :key="index"
                    class="flex cursor-pointer items-start gap-2 p-2 hover:rounded hover:bg-primary-60 hover:dark:bg-primary-950/40"
                    @click="setActiveItem(index)"
                >
                    <CIcon :name="item.icon" class="mt-0.5 shrink-0" />
                    <div class="flex flex-col">
                        <span class="font-medium text-gray-900 dark:text-txt-headings">{{ item.dropdown.text }}</span>
                        <span class="text-xs text-gray-800 dark:text-txt-headings">{{ item.dropdown.description }}</span>
                    </div>
                </div>
            </div>
        </template>
    </CDropdown>
</template>

<script setup lang="ts">
import CButton from '@/components/ui/CButton.vue'
import CDropdown from '@/components/ui/CDropdown.vue'
import CIcon from '@/components/ui/CIcon.vue'
import CTooltip from '@/components/ui/CTooltip.vue'
import { CButtonSizes, type ButtonVariant } from '@/types/buttons'
import type { PropType } from 'vue'
import { computed, ref } from 'vue'

export interface CButtonCombinedType {
    id: string
    icon: string
    button: {
        variant: ButtonVariant
        text: string
    }
    dropdown: {
        text: string
        description: string
    }
}

const props = defineProps({
    items: {
        type: Array as PropType<CButtonCombinedType[]>,
        default: () => [{} as CButtonCombinedType]
    },
    active: {
        type: Number,
        default: 0
    },
    size: {
        type: String as PropType<(typeof CButtonSizes)[keyof typeof CButtonSizes]>,
        default: CButtonSizes.Normal
    },
    tooltip: {
        type: String,
        default: undefined
    }
})

const emit = defineEmits<{
    (e: 'submit', index: number): void
}>()

const dropdown = ref()
const fallbackButton = {
    text: '',
    variant: 'default' as ButtonVariant
}

const activeIndex = computed(() => {
    if (props.active < 0 || props.active >= props.items.length) {
        return 0
    }

    return props.active
})

const activeItem = computed(() => {
    return props.items?.[activeIndex.value]
})

const button = computed(() => {
    return activeItem.value?.button ?? fallbackButton
})

// Dark variants that need white text/icons
const darkVariants = ['gray-750', 'primary', 'primary-dark', 'pink', 'success', 'remove', 'blue-800', 'blue-900']
const isDarkVariant = computed(() => darkVariants.includes(button.value?.variant || ''))

const setActiveItem = (index: number) => {
    emit('submit', index)
    dropdown.value?.close()
}
</script>
